// A paragraph, heading or list item with nothing in it.
const EMPTY_BLOCK = /<(p|h[1-6]|li)(\s[^>]*)?><\/\1>/g;

// A `&nbsp;` in text. One followed by a `>` before any `<` is inside a tag, in an attribute value.
const TEXT_NBSP = /&nbsp;(?![^<]*>)/g;

// A list item holding nothing but a deeper list, which `getSemanticHTML` writes where an item is
// indented more than one level past the item before it.
const INDENT_WRAPPER_CLASS = "list-indent-wrapper";

/**
 * Returns the editor's content as the HTML Sparkle stores, or `""` for an empty editor.
 *
 * The HTML comes from `getSemanticHTML`, not the editor's DOM, which marks every list
 * `<ol>` and a bullet list only by a `data-list` attribute. It is then written out the way
 * the browser serializes HTML, so text holds `'` and `"` as the characters and a typed
 * non-breaking space as `&nbsp;`. Two of `getSemanticHTML`'s choices are undone:
 *   - spaces:      it writes every space in text as `&nbsp;`, which stops text wrapping,
 *                  so each becomes a space again.
 *   - blank lines: it writes one as `<p></p>`, which has no height, so each becomes
 *                  `<p><br></p>`.
 * A list item holding only a deeper list also gets `INDENT_WRAPPER_CLASS`, so the site's
 * list styles give it no marker.
 *
 * @example
 *   // An item indented two levels under "Plans:"
 *   getQuillHTML(quill)
 *   // => '<ul><li>Plans:<ul><li class="list-indent-wrapper"><ul><li>Prescriptions</li></ul></li></ul></li></ul>'
 *
 * @param {import("quill").default} quill
 * @returns {string}
 */
export function getQuillHTML(quill) {
  if (quill.getLength() <= 1) {
    return "";
  }

  const template = document.createElement("template");
  template.innerHTML = quill.getSemanticHTML().replace(TEXT_NBSP, " ");
  template.content.querySelectorAll("li").forEach(markIndentWrapper);

  return template.innerHTML.replace(EMPTY_BLOCK, "<$1$2><br></$1>");
}

function markIndentWrapper(item) {
  const [onlyChild, ...rest] = item.childNodes;

  if (rest.length === 0 && (onlyChild?.nodeName === "UL" || onlyChild?.nodeName === "OL")) {
    item.classList.add(INDENT_WRAPPER_CLASS);
  }
}

/**
 * Replaces the editor's content with `html`.
 *
 * @param {import("quill").default} quill
 * @param {string} [html]
 */
export function setQuillHTML(quill, html) {
  quill.setContents(quill.clipboard.convert({ html: html || "" }));
}
